export interface BranchMove {
  id: string | number;
  parent?: string | number | null;
  priority?: number;
  child_moves?: BranchMove[];
  [key: string]: any;
}

/** Numeric-aware id compare (ids can be a real numeric id or a string temp id). */
function compareIds(a: BranchMove, b: BranchMove): number {
  const an = typeof a.id === 'number' ? a.id : Number(a.id);
  const bn = typeof b.id === 'number' ? b.id : Number(b.id);
  if (!Number.isNaN(an) && !Number.isNaN(bn)) {
    return an - bn;
  }
  return String(a.id).localeCompare(String(b.id));
}

/**
 * The mainline child among a set of siblings: the unique one with
 * priority === 1, or - since legacy/PGN-imported data doesn't always have
 * exactly one (every descendant of a variation can flatly hold priority=2,
 * distinguished only by creation order) - whichever has the smallest id.
 * Mirrors app.study_utils._mainline_child on the backend exactly, so
 * "mainline" is a purely local, per-parent fact (like lichess's
 * children[0]) instead of a chapter-wide invariant.
 */
export function mainlineChild(
  siblings: BranchMove[] | undefined | null,
): BranchMove | undefined {
  if (!siblings?.length) {
    return undefined;
  }
  const ones = siblings.filter(s => (s.priority ?? 1) === 1);
  if (ones.length === 1) {
    return ones[0];
  }
  return siblings.slice().sort(compareIds)[0];
}

/**
 * Flattens the whole chapter move tree (the top-level mainline array plus every
 * variation nested under `child_moves`, at any depth) into an id → move lookup.
 * Needed because replicating the backend's promote/delete walk requires jumping
 * from a move straight to its `parent`, not just down through `child_moves`.
 */
export function flattenMoveTree(
  tree: BranchMove[] | undefined | null,
): Map<string, BranchMove> {
  const byId = new Map<string, BranchMove>();
  const walk = (nodes: BranchMove[] | undefined | null) => {
    if (!nodes?.length) {
      return;
    }
    for (const node of nodes) {
      if (!node) {
        continue;
      }
      if (node.id != null) {
        byId.set(String(node.id), node);
      }
      walk(node.child_moves);
    }
  };
  walk(tree);
  return byId;
}

/** Every direct child of `parentId` among the nodes in `flatById` (pass
 * `null` for the top-level siblings, whose `parent` is null/undefined). */
export function childrenOf(
  flatById: Map<string, BranchMove>,
  parentId: string | number | null | undefined,
): BranchMove[] {
  const key = parentId == null ? null : String(parentId);
  const children: BranchMove[] = [];
  for (const candidate of flatById.values()) {
    const candidateParentKey =
      candidate.parent == null ? null : String(candidate.parent);
    if (candidateParentKey === key) {
      children.push(candidate);
    }
  }
  return children;
}

/**
 * Mirrors the backend's new `promote_branch` (chess/app/study_game.py): walks
 * from `moveId` up through `parent`, and at the *nearest* level where this
 * ancestor isn't already the mainline child of its parent (per
 * `mainlineChild`'s local, fallback-aware rule), returns whichever sibling
 * currently holds that slot — the one that would get displaced if `moveId`
 * were promoted, and so the one to re-promote to undo it.
 *
 * Only the nearest fork is resolved (matches the backend, and this has
 * always been this function's own scope): if a promote actually required
 * swaps at two or more disjoint ancestor levels, re-promoting this single id
 * only reverses the nearest one, not every level the original promote
 * touched — a single re-promote call can't do more than that without
 * widening the manage-branch API to accept a batch of ids.
 */
export function getPromoteUndoTargetId(
  flatById: Map<string, BranchMove>,
  moveId: string | number,
): string | number | null {
  let node = flatById.get(String(moveId));
  if (!node) {
    return null;
  }

  while (node) {
    const parentId = node.parent ?? null;
    const current = mainlineChild(childrenOf(flatById, parentId));
    if (current && String(current.id) !== String(node.id)) {
      return current.id;
    }
    if (parentId == null) {
      return null;
    }
    node = flatById.get(String(parentId));
  }
  return null;
}

/**
 * Counts descendants only (matches `_delete_descendants`'s downward walk) —
 * used for confirmation copy such as "delete this move and the 4 that follow".
 * The backend's upward dead-stub cleanup isn't counted here; it only ever
 * removes now-empty container nodes, not additional user-visible content.
 *
 * Deliberately walks a parent → children index built from `flatById` rather
 * than each node's own `child_moves` array: the API only nests priority>1
 * variations under `child_moves` (see `get_child_moves` in
 * chess/app/study_game.py), while a mainline move's next mainline move is
 * just the following entry in the flat top-level array. The backend's actual
 * delete (`StudyMove.objects.filter(parent=move)`) has no such distinction —
 * deleting a mainline move cascades through every later mainline move too.
 *
 * Uses `childrenOfBatch` (the precomputed-grouping form of `childrenOf`)
 * rather than calling `childrenOf` per node, since this does a full
 * descendant walk instead of a single-level lookup.
 */
export function getDeleteImpactCount(
  flatById: Map<string, BranchMove>,
  moveId: string | number,
): number {
  if (!flatById.has(String(moveId))) {
    return 0;
  }

  const childrenByParent = childrenOfBatch(flatById);

  let count = 0;
  const walk = (id: string) => {
    for (const child of childrenByParent.get(id) || []) {
      count += 1;
      walk(String(child.id));
    }
  };
  walk(String(moveId));
  return count;
}

/** The batch form of `childrenOf`: groups every node in `flatById` by its
 * parent in one pass, for callers that will look up children at many
 * different parent ids (e.g. a full descendant walk) rather than just one.
 * Root-level nodes (parent == null) are omitted - callers needing them
 * should use `childrenOf(flatById, null)` instead. */
function childrenOfBatch(
  flatById: Map<string, BranchMove>,
): Map<string, BranchMove[]> {
  const childrenByParent = new Map<string, BranchMove[]>();
  for (const candidate of flatById.values()) {
    if (candidate.parent == null) {
      continue;
    }
    const key = String(candidate.parent);
    const list = childrenByParent.get(key);
    if (list) {
      list.push(candidate);
    } else {
      childrenByParent.set(key, [candidate]);
    }
  }
  return childrenByParent;
}
